import { useRef, useEffect, FC } from 'react';
import LocalLogicSDK from '@local-logic/sdks-js';
import { Header, LocalLogicMap, LocalLogicSection } from './styles';
import { Block } from '@components/Layout';
import { useTranslations } from 'next-intl';
import { VerticalLineSeparator } from '@components/MiscComponents/VerticalLineSeparator';
import { config } from '@cfg';

declare global {
  interface Window {
    initLocallogic: () => void;
  }
}

interface NeighborhoodLocalLogicProps {
  header?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  countryCode: string;
  geogId: string;
  locale: string;
}

const NeighborhoodLocalLogic: FC<NeighborhoodLocalLogicProps> = ({
  geogId,
  countryCode,
  coordinates,
  header,
  locale,
}) => {
  const t = useTranslations();
  const mapRef = useRef(null);
  const characteristicsRef = useRef(null);
  const schoolRef = useRef(null);
  const marketTrendsRef = useRef(null);

  const globalOptions = (addVariables: boolean) => {
    return {
      locale: locale, // Only supports english and french locales
      appearance: {
        theme: 'day',
        ...(addVariables && {
          variables: {
            '--ll-color-primary': 'black',
            '--ll-color-primary-variant1': 'black',
          },
        }),
      },
    };
  };

  const token = countryCode === 'US' ? config.localLogic.keyUS : config.localLogic.keyCA;

  const ll = LocalLogicSDK(token, globalOptions(false));
  const customizedLL = LocalLogicSDK(token, globalOptions(true));

  useEffect(() => {
    if (geogId) {
      const sdkOptions = {
        geographyId: geogId,
        lat: coordinates.lat,
        lng: coordinates.lng,
        distanceUnit: countryCode === 'US' ? 'imperial' : 'metric',
      };

      const mapSdkInstance = mapRef.current
        ? customizedLL.create('neighborhood-map', mapRef.current, sdkOptions)
        : null;

      const characteristicsSdkInstance = characteristicsRef.current
        ? customizedLL.create(
            'neighborhood-characteristics',
            characteristicsRef.current,
            sdkOptions,
          )
        : null;

      const schoolSdkInstance = schoolRef.current
        ? ll.create('neighborhood-schools', schoolRef.current, sdkOptions)
        : null;
      const marketTrendsSdkInstance = marketTrendsRef.current
        ? customizedLL.create('neighborhood-market-trends', marketTrendsRef.current, sdkOptions)
        : null;

      return () => {
        mapSdkInstance?.destroy();
        characteristicsSdkInstance?.destroy();
        schoolSdkInstance?.destroy();
        marketTrendsSdkInstance?.destroy();
      };
    }
  }, [geogId]);

  return (
    <Block fullOnMobile>
      <Header>{header ? header : t('neighborhoodTitle')}</Header>
      <VerticalLineSeparator />
      <LocalLogicMap ref={mapRef} />
      <LocalLogicSection ref={characteristicsRef} />
      <LocalLogicSection ref={schoolRef} />
      {countryCode === 'US' && <LocalLogicSection ref={marketTrendsRef} />}
    </Block>
  );
};

export default NeighborhoodLocalLogic;
